/* @flow */

import type { Mode } from "state/view-mode";

import React, { useState, useContext, createContext, useEffect, useCallback } from "react";
import { useData, useSendMessage } from "crustate/react";
import { setMode, MODE } from "state/view-mode";
import { ViewModeData } from "data";
import useDevice from "helpers/use-device";

export type Ui = {
  hamburgerOpen: boolean,
  setHamburgerOpen: boolean => void,
  subNavOpen: boolean,
  setSubNavOpen: boolean => void,
  toggleHamburger: string => void,
  closeHamburger: string => void,
  openHamburger: () => void,
  isDesktop: boolean,
  isHamburgerOpening: boolean,
  isHamburgerClosing: boolean,
  setModeAndOpenMenu: Mode => void,
  viewMode: Mode,
  setViewMode: Mode => void,
  filterOCMOpen: boolean,
  setFilterOCMOpen: boolean => void,
  sortOCMOpen: boolean,
  setSortOCMOpen: boolean => void,
};

export type UiProviderProps = {
  children: React$Node,
};

const DESKTOP_MIN_WIDTH = 1057;

export const UiContext = createContext<Ui>({});

export const UiProvider = ({ children }: UiProviderProps) => {
  const sendMessage = useSendMessage();
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const [subNavOpen, setSubNavOpen] = useState(false);
  const [isHamburgerOpening, setIsHamburgerOpening] = useState(false);
  const [isHamburgerClosing, setIsHamburgerClosing] = useState(false);
  const [filterOCMOpen, setFilterOCMOpen] = useState(false);
  const [sortOCMOpen, setSortOCMOpen] = useState(false);
  const viewMode = useData(ViewModeData);
  const isDesktop = useDevice("gt", DESKTOP_MIN_WIDTH);

  useEffect(() => {
    if (viewMode === MODE.NORMAL || viewMode === MODE.CART) {
      setHamburgerOpen(false);
    }
  }, [viewMode, setHamburgerOpen]);

  const openHamburger = () => {
    sendMessage(setMode(MODE.NORMAL));
    setIsHamburgerOpening(true);
    setHamburgerOpen(true);
  };

  const closeHamburger = (duration: string) => {
    setIsHamburgerClosing(true);

    setTimeout(() => {
      setSubNavOpen(false);
      setHamburgerOpen(false);
      setIsHamburgerClosing(false);
      sendMessage(setMode(MODE.NORMAL));
    }, parseInt(duration, 10));
  };

  const toggleHamburger = (duration: string) => hamburgerOpen ?
    closeHamburger(duration) : openHamburger();

  const setModeAndOpenMenu = (mode: Mode) => {
    sendMessage(setMode(mode));
    setSubNavOpen(true);
  };

  const setViewMode = useCallback((mode: Mode) => {
    sendMessage(setMode(mode));

    if (mode === MODE.NORMAL || mode === MODE.CART) {
      setHamburgerOpen(false);
      setSubNavOpen(false);
    }
    else {
      setHamburgerOpen(true);
      setSubNavOpen(true);
    }
  }, [setHamburgerOpen, sendMessage, setSubNavOpen]);

  const defaultContext = {
    hamburgerOpen,
    setHamburgerOpen,
    subNavOpen,
    setSubNavOpen,
    toggleHamburger,
    closeHamburger,
    openHamburger,
    isDesktop,
    isHamburgerOpening,
    isHamburgerClosing,
    setModeAndOpenMenu,
    setViewMode,
    viewMode,
    filterOCMOpen,
    setFilterOCMOpen,
    sortOCMOpen,
    setSortOCMOpen,
  };

  return (
    <UiContext.Provider value={defaultContext}>{children}</UiContext.Provider>
  );
};

export const useUi = (): Ui => {
  const ui = useContext(UiContext);

  if (!ui) {
    throw new Error("useUi(): Usage must be wrapped in a <UiProvider />.");
  }

  return ui;
};
